import React from 'react';
import { Box, Typography, Button, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import DatePickerComponent from 'components/DatePickerComponent';

import { COLORS, PLATFORMS_CONFIG } from './Constants';

const {
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme
} = COLORS;

export type FilterState = {
  selectedPlatforms: string[];
  showSuccess: boolean;
  showError: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  activeQuickFilter: string;
};

export type FilterValidation = {
  isDateInvalid: boolean | null;
  dateErrorMessage?: string;
};

export type FilterActions = {
  togglePlatform: (platformName: string) => void;
  setShowSuccess: (value: boolean) => void;
  setShowError: (value: boolean) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;
  setActiveQuickFilter: (value: string) => void;
  applyFilters: () => void;
  handleClearFilters: () => void;
};

type FiltersSidebarProps = {
  state: FilterState;
  actions: FilterActions;
  validation: FilterValidation;
  isLoading: boolean;
};

const datePickerStyles = {
  '& .MuiInputBase-root': {
    backgroundColor: tableBackgroundColor,
    color: fontColorDarkTheme,
    height: 36,
    fontSize: 13,
  },
  '& .MuiInputBase-input': { padding: '8px 10px' },
  '& .MuiInputLabel-root': {
    color: 'gray',
    fontSize: 12,
    transform: 'translate(12px, 9px) scale(1)',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(12px, -6px) scale(0.75)',
  },
  '& .MuiOutlinedInput-notchedOutline': { 
    borderColor: borderColorDarkTheme 
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#ffb4ab',
    marginLeft: 0,
  }
};

const FiltersSidebar = ({ state, actions, validation, isLoading }: FiltersSidebarProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', p: 3, backgroundColor: tableHeaderColor }}>
      
      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>
        {t('notificationLogs.filters.period')}
      </Typography>

      <Box sx={{ mt: 1, mb: 2 }}>
        <Box sx={datePickerStyles}>
          <DatePickerComponent 
            label={t('notificationLogs.filters.startDate')}
            value={state.startDate}
            disableFuture
            errorMessage={validation.dateErrorMessage} 
            onChange={(val) => { actions.setStartDate(val); actions.setActiveQuickFilter('personalizado'); }} 
          />
          <DatePickerComponent 
            label={t('notificationLogs.filters.endDate')}
            value={state.endDate} 
            disableFuture
            onChange={(val) => { actions.setEndDate(val); actions.setActiveQuickFilter('personalizado'); }} 
          />
        </Box>
      </Box>

      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>{t('notificationLogs.filters.platforms')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4, mt: 1 }}>
        {PLATFORMS_CONFIG.map((plat) => {
          const isSelected = state.selectedPlatforms.includes(plat.name);
          return (
            <Button 
              key={plat.name}
              variant={isSelected ? "contained" : "text"}
              onClick={() => actions.togglePlatform(plat.name)}
              startIcon={plat.icon}
              sx={{ 
                justifyContent: 'flex-start', textTransform: 'none', 
                color: isSelected ? '#fff' : fontColorDarkTheme,
                '& .MuiButton-startIcon': { color: isSelected ? '#fff' : 'gray' }
              }}
            >
              {plat.name}
            </Button>
          );
        })}
      </Box>

      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>{t('notificationLogs.filters.status')}</Typography>
      <FormGroup sx={{ mb: 4, mt: 1 }}>
        <FormControlLabel control={<Checkbox checked={state.showSuccess} onChange={(e) => actions.setShowSuccess(e.target.checked)} sx={{ color: 'gray', '&.Mui-checked': { color: '#58a6ff' } }} />} label={t('notificationLogs.filters.success')} />
        <FormControlLabel control={<Checkbox checked={state.showError} onChange={(e) => actions.setShowError(e.target.checked)} color="error" sx={{ color: 'gray' }} />} label={t('notificationLogs.filters.failure')} />
      </FormGroup>

      <Button fullWidth variant="contained" color="primary" onClick={() => actions.applyFilters()} disabled={isLoading || !!validation.isDateInvalid} sx={{
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: 'rgba(255, 255, 255, 0.4)'
          }
        }}>
        {t('notificationLogs.filters.apply')}
      </Button>
      <Button fullWidth variant="outlined" onClick={actions.handleClearFilters} sx={{ mt: 1, color: 'gray', borderColor: borderColorDarkTheme }}>
        {t('notificationLogs.filters.clear')}
      </Button>
    </Box>
  );
};

export default FiltersSidebar;